import type { TCoreImageStyleProps, TCoreLayoutStyleProps } from '@core/widgets';
import { SemanticColor, semanticColors } from '../../../theme/colors';
import { TVisualOption } from '../../../types/options';

/**
 * Palette key of the visual element.
 */
export type TVisualHierarchy = NonNullable<TVisualOption['iconHierarchy']>;

export type TVisualVariant = NonNullable<TVisualOption['iconVariant']>;

/**
 * Container palette of the visual element: the base colors plus every per-state variant
 * (`disabled`, `hovered`, `pressed`) in a single object. It is spread into CoreLayoutView's
 * `styleProps` as a whole, so the core widget — not the caller — resolves the active state.
 */
export type TVisualContainerPalette = Pick<TCoreLayoutStyleProps, 'backgroundColor' | 'borderColor' | 'disabled' | 'hovered' | 'pressed'>;

/** Icon palette of the visual element: both states in one object, resolved by CoreImageView. */
export type TVisualIconPalette = Pick<TCoreImageStyleProps, 'disabled' | 'iconColor'>;
/**
 * Container palette of the `circle` variant per hierarchy.
 * `hovered` / `pressed` are not part of the map — they are supplied per call site via the `hovered` prop.
 */
export const VISUAL_CIRCLE_MAP: Record<TVisualHierarchy, TVisualContainerPalette> = {
    primary: {
        backgroundColor: semanticColors[SemanticColor.BgEmphasisedMuted],
        disabled: {
            backgroundColor: 'transparent',
            borderColor: semanticColors[SemanticColor.BorderEmphasisedDisabled],
        },
    },
    highlighted: {
        backgroundColor: 'var(--purple_8_tr)',
        disabled: {
            backgroundColor: 'transparent',
            borderColor: 'var(--purple_16_tr)',
        },
    },
    emphasised: {
        backgroundColor: semanticColors[SemanticColor.BgEmphasised],
        disabled: {
            backgroundColor: semanticColors[SemanticColor.BgEmphasisedMuted],
        },
    },
    secondary: {
        backgroundColor: semanticColors[SemanticColor.BgSecondary],
        disabled: {
            backgroundColor: 'transparent',
            borderColor: semanticColors[SemanticColor.BorderPrimaryDisabled],
        },
    },
    secondaryAlt: {
        backgroundColor: 'var(--white)',
        disabled: {
            backgroundColor: 'transparent',
            borderColor: 'var(--purple_16_tr)',
        },
    },
    destructive: {
        backgroundColor: semanticColors[SemanticColor.BgErrorMuted],
        disabled: {
            backgroundColor: 'transparent',
            borderColor: semanticColors[SemanticColor.BorderErrorDisabled],
        },
    },
};

/**
 * Icon palette per variant, shaped as CoreImageView style props.
 * Both states are handed to CoreImageView (`iconColor` / `iconColorDisabled`) so it picks
 * the one matching its own `isDisabled` state.
 */
export const VISUAL_ICON_MAP: Record<TVisualVariant, Record<TVisualHierarchy, TVisualIconPalette>> = {
    simple: {
        primary: { iconColor: semanticColors[SemanticColor.IconPrimary], disabled: { iconColor: semanticColors[SemanticColor.IconPrimaryDisabled] } },
        highlighted: { iconColor: 'var(--white_base)', disabled: { iconColor: 'var(--white_base)' } },
        emphasised: { iconColor: semanticColors[SemanticColor.IconEmphasised], disabled: { iconColor: semanticColors[SemanticColor.IconEmphasisedDisabled] } },
        secondary: { iconColor: semanticColors[SemanticColor.IconSecondary], disabled: { iconColor: semanticColors[SemanticColor.IconPrimaryDisabled] } },
        secondaryAlt: { iconColor: 'var(--black)', disabled: { iconColor: 'var(--grey_40_lc)' } },
        destructive: { iconColor: semanticColors[SemanticColor.IconError], disabled: { iconColor: semanticColors[SemanticColor.IconErrorDisabled] } },
    },
    circle: {
        primary: { iconColor: semanticColors[SemanticColor.IconEmphasised], disabled: { iconColor: semanticColors[SemanticColor.IconEmphasisedDisabled] } },
        highlighted: { iconColor: 'var(--white_base)', disabled: { iconColor: 'var(--white_base)' } },
        emphasised: {
            iconColor: semanticColors[SemanticColor.IconPrimaryOnDarkFill],
            disabled: { iconColor: semanticColors[SemanticColor.IconPrimaryOnDarkFillDisabled] },
        },
        secondary: { iconColor: semanticColors[SemanticColor.IconPrimary], disabled: { iconColor: semanticColors[SemanticColor.IconPrimaryDisabled] } },
        secondaryAlt: { iconColor: 'var(--black)', disabled: { iconColor: 'var(--grey_40_lc)' } },
        destructive: { iconColor: semanticColors[SemanticColor.IconError], disabled: { iconColor: semanticColors[SemanticColor.IconErrorDisabled] } },
    },
};

/** Ring thickness (px) drawn around the circle on hover / press. */
export const HOVER_INSET = 6;
